<template>
  <generic-modal
    :title="$t('payroll detail')"
    ref="generic_modal"
    size="fullscreen"
  >
    <!-- Header Information Card -->
    <div class="card mb-5">
      <header class="card-header has-background-dark">
        <p class="card-header-title has-text-white">
          <span class="icon mr-2">
            <i class="fa fa-calendar"></i>
          </span>
          {{ meta.name }} - {{ format_date_time(meta.create_date) }}
        </p>
        <div class="card-header-icon">
          <span class="tag is-large" :class="getStatusClass(meta.status)">
            <span class="icon">
              <i :class="getStatusIcon(meta.status)"></i>
            </span>
            <span>{{ meta.status ? $t(meta.status) : $t("unknown") }}</span>
          </span>
        </div>
      </header>
      <div class="card-content">
        <div class="columns">
          <!-- Period Information -->
          <div class="column is-4">
            <div class="box has-background-info-dark has-text-white">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fa fa-calendar fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6 has-text-white">{{ $t("period") }}</p>
                  <p class="subtitle is-7 has-text-white">
                    <strong>{{ $t("from") }}:</strong>
                    {{ format_date(meta.from_date) }}<br />
                    <strong>{{ $t("to") }}:</strong>
                    {{ format_date(meta.to_date) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Total Payroll -->
          <div class="column is-4">
            <div class="box has-background-success-dark has-text-white">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fa fa-money fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6 has-text-white">
                    {{ $t("total payroll") }}
                  </p>
                  <p class="title is-4 has-text-white">
                    {{ format_currency(total_payroll) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee Count -->
          <div class="column is-4">
            <div class="box has-background-warning-dark has-text-white">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fa fa-users fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6 has-text-white">{{ $t("employees") }}</p>
                  <p class="title is-4 has-text-white">
                    {{ getEmployeeCount() }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notes Section -->
        <div v-if="meta.settings?.notes" class="notification is-dark">
          <div class="media">
            <div class="media-left">
              <span class="icon">
                <i class="fa fa-sticky-note"></i>
              </span>
            </div>
            <div class="media-content">
              <strong>{{ $t("notes") }}:</strong><br />
              {{ meta.settings.notes }}
            </div>
          </div>
        </div>

        <!-- Salary Settings -->
        <div
          class="notification is-dark"
          :class="meta.settings?.include_salary ? 'is-success' : 'is-warning'"
        >
          <div class="media">
            <div class="media-left">
              <span class="icon">
                <i
                  :class="
                    meta.settings?.include_salary
                      ? 'fa fa-check-circle'
                      : 'fa fa-exclamation-triangle'
                  "
                ></i>
              </span>
            </div>
            <div class="media-content">
              <strong>{{ $t("salary inclusion") }}:</strong>
              {{
                meta.settings?.include_salary
                  ? $t("salary included in calculations")
                  : $t("only bonuses included")
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Employees Table -->
    <div class="title is-5 mb-4">
      <span class="icon mr-2">
        <i class="fa fa-users"></i>
      </span>
      {{ $t("employee details") }}
    </div>

    <div v-if="meta.active_members && meta.active_members.length > 0">
      <div class="table-container">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th>
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-user"></i>
                  </span>
                  <span>{{ $t("name") }}</span>
                </span>
              </th>
              <th v-if="meta.settings?.include_salary">
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-dollar-sign"></i>
                  </span>
                  <span>{{ $t("base salary") }}</span>
                </span>
              </th>
              <th>
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-plus"></i>
                  </span>
                  <span>{{ $t("bonus") }}</span>
                </span>
              </th>
              <th>
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-minus"></i>
                  </span>
                  <span>{{ $t("deductions") }}</span>
                </span>
              </th>
              <th>
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-coins"></i>
                  </span>
                  <span>{{ $t("total") }}</span>
                </span>
              </th>
              <th>
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-info-circle"></i>
                  </span>
                  <span>{{ $t("status") }}</span>
                </span>
              </th>
              <th class="has-text-centered">
                <span class="icon-text">
                  <span class="icon">
                    <i class="fa fa-cogs"></i>
                  </span>
                  <span>{{ $t("actions") }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="member in meta.active_members.filter(
                (m) => m.is_getting_paid
              )"
              :key="member.uuid"
              class="is-hoverable"
            >
              <td class="is-vcentered">
                <div class="media">
                  <div class="media-left">
                    <span class="icon has-text-primary">
                      <i class="fa fa-user-circle"></i>
                    </span>
                  </div>
                  <div class="media-content">
                    <strong
                      >{{ member.first_name }}
                      {{ member.first_surname }}</strong
                    >
                    <!-- <br />
                    <small class="has-text-grey"
                      >{{ member.second_name }}
                      {{ member.second_surname }}</small
                    > -->
                  </div>
                </div>
              </td>
              <td v-if="meta.settings?.include_salary" class="is-vcentered">
                {{ format_currency(member.salary) }}
                <!-- <span class="tag is-primary is-medium">
                  {{ format_currency(member.salary) }}
                </span> -->
              </td>
              <td class="is-vcentered">
                {{ format_currency(member.bonus || 0) }}
                <!-- <span class="tag is-info is-medium">
                  {{ format_currency(member.bonus || 0) }}
                </span> -->
              </td>
              <td class="is-vcentered">
                {{ format_currency(member.deductions || 0) }}
              </td>
              <td class="is-vcentered">
                <!-- {{ format_currency(calculateEmployeeTotal(member)) }} -->
                <span class="is-medium has-text-weight-bold">
                  {{ format_currency(calculateEmployeeTotal(member)) }}
                </span>
              </td>
              <td class="is-vcentered">
                <span
                  class="tag is-medium"
                  :class="getStatusClass(member.status)"
                >
                  <span class="icon">
                    <i :class="getStatusIcon(member.status)"></i>
                  </span>
                  <span>{{ $t(member.status) }}</span>
                </span>
              </td>
              <td class="has-text-centered is-vcentered">
                <button
                  class="button is-small is-primary is-rounded"
                  @click="payroll_approval(member)"
                  :title="$t('view details')"
                >
                  <span class="icon">
                    <i class="fa fa-eye"></i>
                  </span>
                  <!-- <span>{{ $t("view") }}</span> -->
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="has-background-dark">
              <td class="has-text-weight-bold">
                {{ $t("total employees") }}: {{ getEmployeeCount() }}
              </td>
              <td
                v-if="meta.settings?.include_salary"
                class="has-text-weight-bold"
              >
                {{ format_currency(getTotalSalaries()) }}
              </td>
              <td class="has-text-weight-bold">
                {{ format_currency(getTotalBonuses()) }}
              </td>
              <td class="has-text-weight-bold">
                {{ format_currency(getTotalDeductions()) }}
              </td>
              <td class="has-text-weight-bold has-text-success">
                {{ format_currency(total_payroll) }}
              </td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-else class="notification is-info">
      <div class="media">
        <div class="media-left">
          <span class="icon">
            <i class="fa fa-info-circle"></i>
          </span>
        </div>
        <div class="media-content">
          {{ $t("no employees found for this payroll") }}
        </div>
      </div>
    </div>

    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <template v-slot:footer>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon">
          <i class="fa fa-close"></i>
        </span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>
<script>
import {
  put,
  error_message,
  get,
  post,
  format_currency,
  format_date,
  format_date_time,
  generate_error_message_text,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "PayrollModal",
  components: {
    GenericModal,
    GenericDropdown,
  },
  props: {},
  data() {
    return {
      meta: {},
      error: "",
    };
  },
  computed: {
    is_form_invalid() {
      let v = this;
      // return false;
      return (
        !v.meta.name ||
        !v.meta.from_date ||
        !v.meta.to_date ||
        !v.meta.active_members ||
        v.total_payroll <= 0
      );
    },
    total_payroll() {
      let v = this;
      let include_salary = v.meta.settings?.include_salary || false;
      if (!v.meta.active_members) return 0;
      return v.meta.active_members
        .filter((m) => m.is_getting_paid)
        .reduce((sum, member) => {
          let base_salary = include_salary ? Number(member.salary) : 0;
          return sum + base_salary + Number(member.bonus || 0);
        }, 0);
    },
  },
  methods: {
    format_currency,
    format_date,
    format_date_time,
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    open(uuid) {
      let v = this;
      v.set_initial_state();
      get("/staff/payroll", { uuid: uuid })
        .then((response) => {
          v.meta = response;
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.$refs.generic_modal.show_modal();
        });
    },
    set_initial_state() {
      let v = this;
      v.meta = {
        settings: {},
        active_members: [],
      };
      v.error = "";
    },
    payroll_approval(member) {
      window.open(`/staff/payroll/review/${member.uuid}`, "_blank");
    },
    getEmployeeCount() {
      if (!this.meta.active_members) return 0;
      return this.meta.active_members.filter((m) => m.is_getting_paid).length;
    },
    calculateEmployeeTotal(member) {
      let base_salary = this.meta.settings?.include_salary
        ? Number(member.salary || 0)
        : 0;
      return (
        base_salary + Number(member.bonus || 0) - Number(member.deductions || 0)
      );
    },
    getStatusClass(status) {
      const statusClasses = {
        registered: "is-warning",
        pending: "is-warning",
        approved: "is-success",
        rejected: "is-danger",
        sent: "is-warning",
      };
      return statusClasses[status] || "is-dark";
    },
    getStatusIcon(status) {
      const statusIcons = {
        registered: "fa fa-clock-o",
        pending: "fa fa-clock-o",
        approved: "fa fa-check",
        rejected: "fa fa-times",
        sent: "fa fa-paper-plane",
      };
      return statusIcons[status] || "fa fa-question-circle";
    },
    getEmployeeHeaderClass(status) {
      const headerClasses = {
        registered: "has-background-warning",
        pending: "has-background-warning",
        approved: "has-background-success",
        rejected: "has-background-danger",
        sent: "has-background-warning",
      };
      return headerClasses[status] || "has-background-dark";
    },
    getTotalSalaries() {
      if (!this.meta.active_members || !this.meta.settings?.include_salary)
        return 0;
      return this.meta.active_members
        .filter((m) => m.is_getting_paid)
        .reduce((sum, member) => sum + Number(member.salary || 0), 0);
    },
    getTotalBonuses() {
      if (!this.meta.active_members) return 0;
      return this.meta.active_members
        .filter((m) => m.is_getting_paid)
        .reduce((sum, member) => sum + Number(member.bonus || 0), 0);
    },
    getTotalDeductions() {
      if (!this.meta.active_members) return 0;
      return this.meta.active_members
        .filter((m) => m.is_getting_paid)
        .reduce((sum, member) => sum + Number(member.deductions || 0), 0);
    },
  },
  mounted() {},
  created() {},
  watch: {},
};
</script>
<style></style>
